<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import Text from '@uiKit/elements/Text.vue'
import Image from '@uiKit/elements/Image.vue'
import varaLogo from '@assets/images/vara_image.png'
import xrpLedgerLogo from '@assets/images/xrp-ledger-wordmark.png'
import rentalIndexLogo from '@assets/images/rental_income_index.png'
import dldLogo from '@assets/images/dubai_land_dep.png'
import dubaiRestLogo from '@assets/images/dubai-rest-wide.jpg'

// Sidebar footer badge (Figma 36959:4117): the regulators / rails behind
// Sliceal, one card at a time with the slide dots underneath. Same rotation
// contract as the auth showcase — holds while the pointer or keyboard focus is
// inside, skips ticks while the tab is hidden, and never starts for a user who
// asked the OS for reduced motion. Picking a dot restarts the interval so the
// chosen partner gets its full time on screen.
interface SidebarPartner {
  id: string
  title: string
  caption: string
  logo: string
  logoAlt: string
  /** Size of the visible logo window, in px (Figma frame size). */
  frameWidth: number
  frameHeight: number
  /**
   * Where the image sits inside that window, in % of the window — the exports
   * carry wide transparent margins, so the design crops into each one. Omitted
   * means the image simply covers the window.
   */
  crop?: { left: number; top: number; width: number; height: number }
}

const PARTNERS: readonly SidebarPartner[] = [
  {
    id: 'vara',
    title: 'VARA Compliant',
    caption: 'Virtual asset compliance',
    logo: varaLogo,
    logoAlt: 'Virtual Assets Regulatory Authority',
    frameWidth: 53,
    frameHeight: 22,
    crop: { left: -10.98, top: -25.07, width: 121.95, height: 151.61 },
  },
  {
    id: 'xrp-ledger',
    title: 'Blockchain Based',
    caption: 'Transparent and secure.',
    logo: xrpLedgerLogo,
    logoAlt: 'XRP Ledger',
    frameWidth: 56,
    frameHeight: 14,
  },
  {
    id: 'rental-income-index',
    title: 'Rental Income Index',
    caption: 'Official govt-backed metrics.',
    logo: rentalIndexLogo,
    logoAlt: 'Rental Income Index',
    frameWidth: 67,
    frameHeight: 30,
    crop: { left: 19.4, top: 4.15, width: 58.21, height: 90.2 },
  },
  {
    id: 'dld',
    title: 'DLD Regulated',
    caption: 'Legally and indisputable.',
    logo: dldLogo,
    logoAlt: 'Dubai Land Department',
    frameWidth: 56,
    frameHeight: 16,
    crop: { left: 0, top: -33.94, width: 100, height: 164.92 },
  },
  {
    id: 'dubai-rest',
    title: 'Dubai REST',
    caption: 'Officially regulated by the Govt.',
    logo: dubaiRestLogo,
    logoAlt: 'Dubai REST',
    frameWidth: 67,
    frameHeight: 30,
    crop: { left: -0.35, top: -25.05, width: 99.98, height: 154.93 },
  },
]

const ROTATION_INTERVAL_MS = 3000

const activeIndex = ref(0)
const isPaused = ref(false)
let rotationTimer: ReturnType<typeof setInterval> | null = null

function frameStyle(partner: SidebarPartner): Record<string, string> {
  return { width: `${partner.frameWidth}px`, height: `${partner.frameHeight}px` }
}

function cropStyle(partner: SidebarPartner): Record<string, string> | undefined {
  if (!partner.crop) return undefined
  const { left, top, width, height } = partner.crop
  return { left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }
}

function advance(): void {
  if (isPaused.value || document.hidden) return
  activeIndex.value = (activeIndex.value + 1) % PARTNERS.length
}

function stopRotation(): void {
  if (rotationTimer) clearInterval(rotationTimer)
  rotationTimer = null
}

function startRotation(): void {
  stopRotation()
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  rotationTimer = setInterval(advance, ROTATION_INTERVAL_MS)
}

function showPartner(index: number): void {
  activeIndex.value = index
  startRotation()
}

onMounted(startRotation)
onUnmounted(stopRotation)
</script>

<template>
  <Box
    is="section"
    direction="col"
    align="center"
    gap="2"
    block
    aria-roledescription="carousel"
    aria-label="Regulated and recorded by"
    @mouseenter="isPaused = true"
    @mouseleave="isPaused = false"
    @focusin="isPaused = true"
    @focusout="isPaused = false"
  >
    <!-- Every slide stays mounted and stacked in one grid cell, so the card
         keeps a steady height and the crossfade never jumps. -->
    <Box class="partner-card" block>
      <Box
        v-for="(partner, index) in PARTNERS"
        :key="partner.id"
        flex
        align="center"
        gap="2"
        role="group"
        aria-roledescription="slide"
        :aria-label="`${index + 1} of ${PARTNERS.length}`"
        :aria-hidden="index !== activeIndex"
        :class="['partner-card__slide', { 'partner-card__slide--active': index === activeIndex }]"
      >
        <Box flex align="center" justify="center" class="partner-card__mark">
          <Box class="partner-card__frame" :style="frameStyle(partner)">
            <Image
              :src="partner.logo"
              :alt="partner.logoAlt"
              :class="['partner-card__logo', { 'partner-card__logo--cover': !partner.crop }]"
              :style="cropStyle(partner)"
            />
          </Box>
        </Box>
        <Box direction="col" grow class="partner-card__text">
          <Text is="span" type="caption" weight="semibold" color="default" truncate>
            {{ partner.title }}
          </Text>
          <Text is="span" type="caption-sm" weight="regular" truncate>
            {{ partner.caption }}
          </Text>
        </Box>
      </Box>
    </Box>

    <Box flex align="center" gap="1">
      <Button
        v-for="(partner, index) in PARTNERS"
        :key="partner.id"
        variant="text"
        :aria-label="`Show ${partner.title}`"
        :aria-current="index === activeIndex ? 'true' : undefined"
        class="partner-dots__hit"
        @click="showPartner(index)"
      >
        <Box
          :class="['partner-dots__dot', { 'partner-dots__dot--active': index === activeIndex }]"
        />
      </Button>
    </Box>
  </Box>
</template>

<style scoped>
.partner-card {
  display: grid;
  padding: 7px 9px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-200);
  border-radius: 12px;
  filter: drop-shadow(0 0 2.5px var(--p-primary-50));
  box-sizing: border-box;
}

.partner-card__slide {
  grid-area: 1 / 1;
  min-width: 0;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.4s ease,
    visibility 0.4s;
}

.partner-card__slide--active {
  opacity: 1;
  visibility: visible;
}

/* Fixed logo column so the text starts at the same x on every slide; the wider
   frames (67px) are clipped to it, exactly as in the design. */
.partner-card__mark {
  width: 60px;
  height: 36px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 8px;
}

.partner-card__frame {
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
}

.partner-card__logo {
  position: absolute;
  max-width: none;
}

.partner-card__logo--cover {
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.partner-card__text {
  min-width: 0;
}

/* The pill is 6px tall; the button around it stretches the hit area without
   pushing the pills apart. */
.partner-dots__hit {
  padding: 0.5rem 0 !important;
}

.partner-dots__dot {
  width: 8px;
  height: 6px;
  border-radius: 6px;
  background: var(--p-surface-200);
  transition:
    width 0.3s ease,
    background-color 0.3s ease;
}

.partner-dots__dot--active {
  width: 19px;
  background: var(--p-primary-600);
}

@media (prefers-reduced-motion: reduce) {
  .partner-card__slide,
  .partner-dots__dot {
    transition: none;
  }
}
</style>
